import * as Accordion from "@radix-ui/react-accordion";
import { H6 } from "src/components/HeadingV2/HeadingV2.tsx";
import { Flex } from "src/components/Flex.tsx";
import { SelectV2 } from "src/components/SelectV2/SelectV2.tsx";
import * as styles from "./PriceListRow.style.tsx";
import {
  PriceListCalculateType,
  PriceListItemCalculateType,
  type PriceListResource,
} from "@exonn/js-sdk";
import { useFormContext, useWatch } from "react-hook-form";
import { formatPercent, formatPrice, formatPriceV2 } from "@exonn/utils";
import { computeFinalPriceListItemPrice } from "src/admin/modules/product/utils/computeFinalPriceListItemPrice.ts";
import { type MouseEvent } from "react";
import { useList } from "src/system/useList.tsx";
import { ResourceEnum } from "src/constants/constants.ts";
import { computePriceListItemFromPriceList } from "src/admin/modules/product/utils/computePriceListItemFromPriceList.tsx";
import { formatPriceListDateRange } from "src/components/Form/panels/PricePanel/utils/formatPriceListDateRange.ts";
import { getCalculateTypeOptions } from "src/admin/modules/product/shared/getCalculateTypeOptions.ts";
import { NumberField } from "src/components/Form/fields/NumberField.tsx";
import type { FormValues } from "src/admin/forms/variant/config.tsx";
import { useTranslation } from "react-i18next";
import { TranslationNamespaceEnum } from "src/lib/i18next/types.ts";
import { useResourceFormResource } from "src/components/Form/hooks/useResourceFormResource.ts";
import { getCalculateValueAdornment } from "src/components/Form/panels/PricePanel/utils/getCalculateValueAdornment.ts";

export function PriceListRow({ id }: { id: string }) {
  const { t } = useTranslation(TranslationNamespaceEnum.PricePanel);
  const resource = useResourceFormResource();

  const { setValue, control, getValues } = useFormContext<FormValues>();
  const { data } = useList<PriceListResource>({
    resource: ResourceEnum.PriceList,
    include: ["currency"],
  });
  const priceLists = data?.data || [];

  const priceList = priceLists.find((priceList) => priceList.id === id)!;

  const childPrice = useWatch({
    name: "price",
    control,
  });
  const parentPrice = useWatch({
    name: "parent_price",
    control,
    disabled: resource !== ResourceEnum.Variant,
  });
  const priceInherited = useWatch({
    name: "price_inherited",
    control,
    disabled: resource !== ResourceEnum.Variant,
  });
  const parentOriginalPriceListValues = useWatch({
    name: `parentPriceListItems.${id}`,
    control,
    disabled: resource !== ResourceEnum.Variant,
  });
  const childOriginalPriceListValues = useWatch({
    name: `priceListItems.${id}`,
    control,
  });

  const price = priceInherited ? parentPrice : childPrice;
  const originalPriceListValues = priceInherited
    ? parentOriginalPriceListValues
    : childOriginalPriceListValues;
  const priceListValues =
    originalPriceListValues ??
    computePriceListItemFromPriceList({
      priceList,
    });
  const { calculate_type } = priceListValues;

  const handleTypeChange = (
    type: "abs" | "percent" | "fixed" | "" | "none",
  ) => {
    if (type === "none" || type === "") {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const { [id]: _, ...nextPriceListItems } =
        getValues("priceListItems") ?? {};
      setValue("priceListItems", nextPriceListItems);
    } else {
      setValue(
        `priceListItems.${id}`,
        {
          calculate_type: type as unknown as PriceListItemCalculateType,
          calculate_value: "0",
          calculate_from: "selling_price",
          is_calculate_round: false,
        },
        {
          shouldDirty: true,
        },
      );
    }
  };

  const handleDelete = (e: MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    setValue(
      `priceListItems.${id}`,
      {
        calculate_type: PriceListItemCalculateType.Inactive,
        calculate_value: "0",
        calculate_from: "selling_price",
        is_calculate_round: false,
      },
      {
        shouldDirty: true,
      },
    );
  };

  const variantPriceOrInheritedParentPrice = priceInherited
    ? parentPrice!
    : price;

  const priceListItemPrice = computeFinalPriceListItemPrice({
    priceListItem: priceListValues,
    price: +variantPriceOrInheritedParentPrice,
  });

  if (calculate_type === PriceListItemCalculateType.Inactive) {
    return null;
  }

  return (
    <Accordion.Item css={styles.itemStyle} value={priceList?.title}>
      <Accordion.Trigger css={styles.triggerStyle}>
        <Flex
          alignItems={"center"}
          justifyContent={"space-between"}
          style={{ width: "100%" }}
        >
          <Flex flexDirection={"column"} alignItems={"flex-start"}>
            <H6>{priceList.title}</H6>
            <label css={styles.dateLabel}>
              {formatPriceListDateRange({
                priceList,
                t,
              })}
            </label>
          </Flex>
          <Flex alignItems={"center"}>
            <label css={styles.typeLabel()}>
              {formatPriceV2(priceListItemPrice)}{" "}
              {t(`panel.${priceList.is_net_show ? "net" : "gross"}`)}{" "}
              {priceList.currency?.iso}
            </label>
            {!priceInherited && (
              <div css={styles.deleteIconContainer} onClick={handleDelete}>
                <i className={"icon-15-delete"} />
              </div>
            )}

            <div css={styles.chevronContainer}>
              <i css={styles.chevron} className={"icon-15-arw_dw"} />
            </div>
          </Flex>
        </Flex>
      </Accordion.Trigger>

      <Accordion.Content css={styles.contentStyle}>
        <div css={styles.contentInner}>
          <SelectV2
            allowClearValue={false}
            disabled={priceInherited}
            value={!originalPriceListValues ? "none" : calculate_type}
            onChange={handleTypeChange}
            options={getCalculateTypeOptions({
              t,
              belongsToAutomaticPriceList: priceList.calculate_type !== null,
            })}
          />

          {!originalPriceListValues ? (
            <>
              {priceList.calculate_type === PriceListCalculateType.Absolute && (
                <div css={styles.listPriceText}>
                  {formatPrice(+price)}+
                  {formatPriceV2(
                    priceListValues.calculate_value
                      ? +priceListValues.calculate_value
                      : 0,
                    priceList?.currency,
                  )}
                </div>
              )}
              {priceList.calculate_type === PriceListCalculateType.Percent && (
                <div css={styles.listPriceText}>
                  {formatPrice(+price)}+
                  {formatPercent(
                    priceListValues.calculate_value
                      ? +priceListValues.calculate_value
                      : 0,
                  )}
                  {"%"}
                </div>
              )}
            </>
          ) : (
            <>
              {calculate_type !== null && (
                <Flex css={{ width: 290, gap: 10 }}>
                  <NumberField
                    disabled={priceInherited}
                    controls={false}
                    control={control}
                    name={`priceListItems.${priceList.id}.calculate_value`}
                  />
                  <Flex
                    alignItems={"center"}
                    style={{
                      height: 37,
                    }}
                  >
                    {getCalculateValueAdornment({
                      priceList,
                      calculate_type,
                    })}
                  </Flex>
                </Flex>
              )}
            </>
          )}
        </div>
      </Accordion.Content>
    </Accordion.Item>
  );
}
